import { useEffect, useRef } from 'react';

import {
  classifyCookieSignal,
  clearConsentCheckpoint,
  consumeExpectedConsentWrite,
  MAX_POLL_ATTEMPTS,
  POLL_INTERVAL_MS,
  readConsentFromCookie,
  writeTollhouseConsent
} from 'hooks/useTrustArcConsent';
import type { ConsentStatus } from 'types/trustarc';

interface UseConsentEventBridgeOptions {
  accountConfig?: { url: string };
  isAuthenticated: boolean;
}

/**
 * Global consent event bridge. Listens for TrustArc banner Accept/Decline clicks
 * via the prefclose callback and immediately writes the choice to Tollhouse.
 *
 * Runs on every page (not just Privacy UI). This ensures banner clicks from any page
 * persist to the durable store without waiting for the next page-load reconciliation.
 *
 * Coordination with useConsentReconciliation:
 * - When reconciliation calls setConsentViaApi (SYNC_COOKIE_REJECT), it records an
 *   expected write. This bridge checks consumeExpectedConsentWrite() and skips the
 *   Tollhouse POST when the prefclose was triggered by our own CMA write, not a user action.
 */
const useConsentEventBridge = ({ accountConfig, isAuthenticated }: UseConsentEventBridgeOptions): void => {
  const isAuthenticatedRef = useRef(isAuthenticated);
  const accountConfigRef = useRef(accountConfig);
  const lastKnownStatusRef = useRef<ConsentStatus | null>(null);
  const originalPrefCloseRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  useEffect(() => {
    accountConfigRef.current = accountConfig;
  }, [accountConfig]);

  useEffect(() => {
    let hasPatched = false;
    let attempts = 0;
    let interval: ReturnType<typeof setInterval> | null = null;

    const stopPolling = (): void => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const initialize = (): void => {
      const eu = window.truste?.eu;
      attempts += 1;

      if (!hasPatched && eu?.prefclose) {
        originalPrefCloseRef.current = eu.prefclose;
        lastKnownStatusRef.current = readConsentFromCookie();

        eu.prefclose = () => {
          originalPrefCloseRef.current?.();

          const newStatus = readConsentFromCookie();

          // Skip if this prefclose was triggered by our own CMA write (reconciliation or Privacy Save)
          if (consumeExpectedConsentWrite(newStatus)) {
            return;
          }

          // Skip EU users — consent is handled by TrustArc's own EU flow
          if (classifyCookieSignal() === 'EU') {
            return;
          }

          // Skip unauthenticated users — Tollhouse requires auth
          if (!isAuthenticatedRef.current || !accountConfigRef.current) {
            return;
          }

          // Skip duplicate writes — prefclose fires multiple times
          if (newStatus === lastKnownStatusRef.current) {
            return;
          }

          lastKnownStatusRef.current = newStatus;
          writeTollhouseConsent(accountConfigRef.current, newStatus).then(success => {
            if (!success) {
              clearConsentCheckpoint();
            }
          });
        };

        hasPatched = true;
      }

      if (hasPatched || attempts >= MAX_POLL_ATTEMPTS) {
        stopPolling();
      }
    };

    initialize();
    if (!hasPatched) {
      interval = setInterval(initialize, POLL_INTERVAL_MS);
    }

    return () => {
      stopPolling();
      if (hasPatched && window.truste?.eu && originalPrefCloseRef.current) {
        window.truste.eu.prefclose = originalPrefCloseRef.current;
      }
    };
  }, []);
};

export default useConsentEventBridge;
